import React, {useState} from 'react';
import {Box, Typography, TextField, Button, Chip} from '@mui/material';
import {useGetNativeComponent} from '../../../hooks/productions/useProductions';
import ArrButton from '../../../components/common/arr-button/ArrButton';

const NativeComponentApiTest: React.FC = () => {
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [productionId, setProductionId] = useState<number>(1017);
  const [moduleType, setModuleType] = useState<string>('Production');
  const [exportProductionId, setExportProductionId] = useState<number>(0);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  // ✅ Hook will automatically fetch when enabled is true
  const {
    data: nativeComponentData,
    isLoading,
    error,
    refetch,
  } = useGetNativeComponent(
    {
      SecurityGroupId: securityGroupId,
      moduletype: moduleType,
      productionId: productionId,
      exportProductionId: exportProductionId,
    },
    enabled // Only fetch when user clicks "Test API"
  );

  const handleTestApi = () => {
    setEnabled(true);
  };

  const handleReset = () => {
    setEnabled(false);
    // Note: React Query will keep the cached data, but we stop fetching
  };

  const handleCopyJson = async () => {
    if (nativeComponentData) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(nativeComponentData, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        🧪 Native Component API Test
      </Typography>
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          marginBottom: 3,
        }}
      >
        <Typography variant="h6" className="typo-text subhead2_size">
          Request Parameters
        </Typography>

        <Box sx={{display: 'flex', gap: 2, marginTop: 2, flexWrap: 'wrap'}}>
          <TextField
            label="Security Group ID"
            type="number"
            value={securityGroupId}
            onChange={(e) => setSecurityGroupId(Number(e.target.value))}
            size="small"
          />
          <TextField
            label="Module Type"
            type="text"
            value={moduleType}
            onChange={(e) => setModuleType(e.target.value)}
            size="small"
            placeholder="Production"
          />
          <TextField
            label="Production ID"
            type="number"
            value={productionId}
            onChange={(e) => setProductionId(Number(e.target.value))}
            size="small"
          />
          <TextField
            label="Export Production ID"
            type="number"
            value={exportProductionId}
            onChange={(e) => setExportProductionId(Number(e.target.value))}
            size="small"
          />
        </Box>

        <Box sx={{marginTop: 2, display: 'flex', gap: 2}}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTestApi}
            data-testid="native-component-api-test-test-api-button">
            🚀 Test API
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            data-testid="native-component-api-test-reset-button">
            🔄 Reset
          </Button>
          {enabled && (
            <Button
              variant="outlined"
              onClick={() => refetch()}
              data-testid="native-component-api-test-refetch-button">
              ♻️ Refetch
            </Button>
          )}
        </Box>
      </Box>
      {/* Loading State */}
      {isLoading && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #2196f3',
          }}
        >
          <Typography className="typo-text body2_size">
            ⏳ Loading... Making API request...
          </Typography>
        </Box>
      )}
      {/* Error State */}
      {error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Error
          </Typography>
          <Typography className="typo-text body3_size" color="error">
            {error.message}
          </Typography>
        </Box>
      )}
      {/* Success State - Display Response */}
      {nativeComponentData && !isLoading && (
        <Box>
          <Typography
            variant="h6"
            className="typo-text subhead2_size"
            gutterBottom
          >
            ✅ API Response
          </Typography>

          {/* Summary */}
          <Box
            sx={{
              padding: 2,
              backgroundColor: '#e8f5e9',
              borderRadius: 1,
              marginBottom: 2,
              border: '1px solid #4caf50',
            }}
          >
            <Typography className="typo-text label2_size">
              📊 Response Summary
            </Typography>
            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', marginTop: 1}}>
              <Chip
                label={`Select All: ${nativeComponentData.isSelectAllFileTypes ? 'Yes' : 'No'}`}
                color={
                  nativeComponentData.isSelectAllFileTypes
                    ? 'success'
                    : 'default'
                }
                size="small"
              />
              <Chip
                label={`Produce Load File: ${nativeComponentData.produceLoadFile ? 'Yes' : 'No'}`}
                color={
                  nativeComponentData.produceLoadFile ? 'success' : 'default'
                }
                size="small"
              />
              <Chip
                label={`HTML Emails: ${nativeComponentData.generateHTMLforEmails ? 'Yes' : 'No'}`}
                color={
                  nativeComponentData.generateHTMLforEmails
                    ? 'success'
                    : 'default'
                }
                size="small"
              />
              <Chip
                label={`Native Tech Docs: ${nativeComponentData.produceNativeForTechDocs ? 'Yes' : 'No'}`}
                color={
                  nativeComponentData.produceNativeForTechDocs
                    ? 'success'
                    : 'default'
                }
                size="small"
              />
              <Chip
                label={`Locked Files: ${nativeComponentData.producedLockedFiles ? 'Yes' : 'No'}`}
                color={
                  nativeComponentData.producedLockedFiles
                    ? 'success'
                    : 'default'
                }
                size="small"
              />
              <Chip
                label={`Excel Protection: ${nativeComponentData.protectionOnExcelFile ? 'Yes' : 'No'}`}
                color={
                  nativeComponentData.protectionOnExcelFile
                    ? 'success'
                    : 'default'
                }
                size="small"
              />
              <Chip
                label={`Exclude Parent Native: ${nativeComponentData.shouldExcludeNativeOfParent ? 'Yes' : 'No'}`}
                color={
                  nativeComponentData.shouldExcludeNativeOfParent
                    ? 'info'
                    : 'default'
                }
                size="small"
              />
            </Box>
            <Typography className="typo-text body3_size" sx={{marginTop: 1}}>
              • Selected File Types:{' '}
              {nativeComponentData.selectedFileTypes?.length || 0}
            </Typography>
            <Typography className="typo-text body3_size">
              • Available File Types:{' '}
              {nativeComponentData.availableFileTypes?.length || 0}
            </Typography>
            <Typography className="typo-text body3_size">
              • Native File Type Count:{' '}
              {nativeComponentData.selectedNativeFileType?.length || 0}
            </Typography>
            <Typography className="typo-text body3_size">
              • eRedacted & Privileged Family:{' '}
              {nativeComponentData.eRedactedAndPrevilededFamily}
            </Typography>
            <Typography className="typo-text body3_size">
              • Native Selected File Groups:{' '}
              {nativeComponentData.nativeSelectedFileGroups || 'None'}
            </Typography>
          </Box>

          {/* Full JSON Response */}
          <Box sx={{marginTop: 2}}>
            <Box sx={{marginBottom: 1}}>
              <ArrButton
                variant={copySuccess ? 'secondary' : 'primary'}
                size="small"
                onClick={handleCopyJson}
                dataTestId="native-component-api-test-button">
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </ArrButton>
            </Box>
            <Box
              sx={{
                padding: 2,
                backgroundColor: '#263238',
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: '500px',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{color: '#fff', marginBottom: 1}}
              >
                📄 Full JSON Response
              </Typography>
              <pre
                style={{
                  color: '#4caf50',
                  fontSize: '12px',
                  margin: 0,
                  fontFamily: 'monospace',
                }}
              >
                {JSON.stringify(nativeComponentData, null, 2)}
              </pre>
            </Box>
          </Box>

          {/* Individual Sections */}
          <Box sx={{marginTop: 3}}>
            <Typography variant="h6" className="typo-text subhead3_size">
              📋 Data Breakdown
            </Typography>

            {/* Selected File Types */}
            <Box sx={{marginTop: 2}}>
              <Typography className="typo-text label2_size">
                Selected File Types (
                {nativeComponentData.selectedFileTypes?.length || 0})
              </Typography>
              <Box
                sx={{
                  padding: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  marginTop: 1,
                }}
              >
                {nativeComponentData.selectedFileTypes &&
                nativeComponentData.selectedFileTypes.length > 0 ? (
                  <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                    {nativeComponentData.selectedFileTypes.map(
                      (fileType, index) => (
                        <Chip
                          key={index}
                          label={`${fileType.fileTypeLabel} (ID: ${fileType.fileTypeID})`}
                          color={
                            fileType.isFileTypeSelected ? 'primary' : 'default'
                          }
                          size="small"
                        />
                      )
                    )}
                  </Box>
                ) : (
                  <Typography className="typo-text body3_size">
                    No selected file types
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Available File Types */}
            <Box sx={{marginTop: 2}}>
              <Typography className="typo-text label2_size">
                Available File Types (
                {nativeComponentData.availableFileTypes?.length || 0})
              </Typography>
              <Box
                sx={{
                  padding: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  marginTop: 1,
                  maxHeight: '200px',
                  overflow: 'auto',
                }}
              >
                <pre style={{fontSize: '12px', margin: 0}}>
                  {JSON.stringify(
                    nativeComponentData.availableFileTypes || [],
                    null,
                    2
                  )}
                </pre>
              </Box>
            </Box>

            {/* Selected Native File Types */}
            <Box sx={{marginTop: 2}}>
              <Typography className="typo-text label2_size">
                Selected Native File Types (
                {nativeComponentData.selectedNativeFileType?.length || 0})
              </Typography>
              <Box
                sx={{
                  padding: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  marginTop: 1,
                }}
              >
                {nativeComponentData.selectedNativeFileType &&
                nativeComponentData.selectedNativeFileType.length > 0 ? (
                  <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                    {nativeComponentData.selectedNativeFileType.map(
                      (fileType, index) => (
                        <Chip key={index} label={fileType} size="small" />
                      )
                    )}
                  </Box>
                ) : (
                  <Typography className="typo-text body3_size">
                    No selected native file types
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Additional Info */}
            <Box sx={{marginTop: 2}}>
              <Typography className="typo-text label2_size">
                Additional Information
              </Typography>
              <Box
                sx={{
                  padding: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  marginTop: 1,
                }}
              >
                <Typography className="typo-text body3_size">
                  <strong>Native Component Model:</strong>{' '}
                  {nativeComponentData.nativeComponentmodel}
                </Typography>
                <Typography className="typo-text body3_size">
                  <strong>Selected File Groups:</strong>{' '}
                  {nativeComponentData.nativeSelectedFileGroups}
                </Typography>
                <Typography className="typo-text body3_size">
                  <strong>Selected Tags:</strong>{' '}
                  {nativeComponentData.nativeSelectedTags}
                </Typography>
                <Typography className="typo-text body3_size">
                  <strong>Selected Tag Labels:</strong>{' '}
                  {nativeComponentData.nativeSelectedTagLabels}
                </Typography>
                <Typography className="typo-text body3_size">
                  <strong>eRedacted & Privileged Family:</strong>{' '}
                  {nativeComponentData.eRedactedAndPrevilededFamily}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      {/* Initial State or No Data */}
      {!isLoading && !nativeComponentData && !error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#fff3e0',
            borderRadius: 1,
            border: '1px solid #ff9800',
          }}
        >
          <Typography className="typo-text body2_size">
            {!enabled
              ? '👆 Click "Test API" button to make the API request'
              : '⏳ Preparing request...'}
          </Typography>
        </Box>
      )}
      {/* Debug Info (helpful for troubleshooting) */}
      {enabled && !isLoading && !nativeComponentData && !error && (
        <Box
          sx={{
            padding: 2,
            backgroundColor: '#fff9e6',
            borderRadius: 1,
            border: '1px solid #ffc107',
            marginBottom: 2,
          }}
        >
          <Typography className="typo-text label2_size">
            🔍 Debug Info
          </Typography>
          <Typography className="typo-text body3_size">
            Enabled: {enabled ? 'Yes' : 'No'} | Loading:{' '}
            {isLoading ? 'Yes' : 'No'} | Has Data:{' '}
            {nativeComponentData ? 'Yes' : 'No'} | Has Error:{' '}
            {error ? 'Yes' : 'No'}
          </Typography>
          <Typography className="typo-text body3_size" sx={{marginTop: 1}}>
            If you see this, the request may not have triggered. Try clicking
            Refetch.
          </Typography>
        </Box>
      )}
      {/* Network Details */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#fafafa'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          🌐 Network Details
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Endpoint:</strong>{' '}
          /productions/api/v1/production/view/nativecomponent
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Method:</strong> GET
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Query Params:</strong>
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {`SecurityGroupId=${securityGroupId}
moduletype=${moduleType}
productionId=${productionId}
exportProductionId=${exportProductionId}`}
        </pre>
        <Typography className="typo-text body3_size">
          <strong>Base URL:</strong>{' '}
          https://arr-api-productions-dev.us2kd2w30.k8d.consiliotest.com/
        </Typography>
      </Box>
    </Box>
  );
};

export default NativeComponentApiTest;
